import {useEffect, useState} from "react";
import {Label} from "../../../basic/label";
import {Input} from "../../../basic/input";
import {Button} from "../../../basic/button";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "../../../basic/select";
import {CreditCard, ExternalLink} from "lucide-react";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {
    useGetCustomerRedirectUrlQuery,
    useUpdateTeamByIdMutation,
    VwpTeam
} from "../../../../store/generated/api";
import {toast} from "sonner";
import {CollapsibleSection} from "../../../shared/CollapsibleSection";

interface TeamBillingInfoProps {
    selectedTeam: VwpTeam;
    onTeamUpdate?: () => void;
}

export function TeamBillingInfo({selectedTeam, onTeamUpdate}: TeamBillingInfoProps) {
    const {t} = useTranslationWithKeys();
    const {handleApiError} = useErrorHandler();
    const [name, setName] = useState(selectedTeam.name || "");
    const [billingStreetAddress, setBillingStreetAddress] = useState(selectedTeam.streetAddress || "");
    const [billingCity, setBillingCity] = useState(selectedTeam.city || "");
    const [billingState, setBillingState] = useState(selectedTeam.stateProvince || "");
    const [billingPostalCode, setBillingPostalCode] = useState(selectedTeam.postalCode || "");
    const [billingCountry, setBillingCountry] = useState(selectedTeam.country || "");
    const [billingEmail, setBillingEmail] = useState(selectedTeam.billingEmail || "");
     const [invoiceLanguage, setInvoiceLanguage] = useState(selectedTeam.invoiceLanguage || "");
     const [phone, setPhone] = useState(selectedTeam.phoneNumber || "");

     const [website, setWebsite] = useState(selectedTeam.website || "");
    const [vatNumber, setVatNumber] = useState(selectedTeam.vatNumber || "");
    const [stripeCustomerId, setStripeCustomerId] = useState(selectedTeam.stripeCustomerId || "");
    const [updateTeamById, {isLoading: isUpdating}] = useUpdateTeamByIdMutation();

     useEffect(() => {
         setName(selectedTeam.name || "");
         setBillingStreetAddress(selectedTeam.streetAddress || "");
         setBillingCity(selectedTeam.city || "");
         setBillingState(selectedTeam.stateProvince || "");
         setBillingPostalCode(selectedTeam.postalCode || "");
         setBillingCountry(selectedTeam.country || "");
         setBillingEmail(selectedTeam.billingEmail || "");
         setInvoiceLanguage(selectedTeam.invoiceLanguage || "");
         setPhone(selectedTeam.phoneNumber || "");
        setWebsite(selectedTeam.website || "");
        setVatNumber(selectedTeam.vatNumber || "");
        setStripeCustomerId(selectedTeam.stripeCustomerId || "");
    }, [selectedTeam]);

    const {data: redirectData, isLoading: isLoadingRedirect} = useGetCustomerRedirectUrlQuery(
        {email: billingEmail || selectedTeam.adminEmail || ""},
        {skip: !billingEmail && !stripeCustomerId}
    );

    const canOpenStripe = !!billingEmail || !!stripeCustomerId;

    const hasChanges = () => {
        return name !== selectedTeam.name ||
            billingStreetAddress !== selectedTeam.streetAddress ||
            billingCity !== selectedTeam.city ||
            billingState !== selectedTeam.stateProvince ||
            billingPostalCode !== selectedTeam.postalCode ||
            billingCountry !== selectedTeam.country ||
            billingEmail !== selectedTeam.billingEmail ||
            invoiceLanguage !== selectedTeam.invoiceLanguage ||
            phone !== selectedTeam.phoneNumber ||
            website !== selectedTeam.website ||
            vatNumber !== selectedTeam.vatNumber ||
            stripeCustomerId !== selectedTeam.stripeCustomerId;
    };

    const handleSaveBillingInfo = async () => {
        if (!hasChanges()) return;

        try {
            await updateTeamById({
                teamId: selectedTeam.id,
                vwpTeamUpdateRequest: {
                    name: name || undefined,
                    billingStreetAddress: billingStreetAddress || undefined,
                    billingCity: billingCity || undefined,
                    billingState: billingState || undefined,
                    billingPostalCode: billingPostalCode || undefined,
                    billingCountry: billingCountry || undefined,
                    billingEmail: billingEmail || undefined,
                    invoiceLanguage: invoiceLanguage as "en" | "de" | "fr" | undefined,
                    phone: phone || undefined,
                    website: website || undefined,
                    vatNumber: vatNumber || undefined,
                    stripeCustomerId: stripeCustomerId || undefined
                }
            }).unwrap();

            toast.success(t('admin.teams.teamManagement.billingInfoUpdated'));
            onTeamUpdate?.();
        } catch (error: any) {
            handleApiError(error, {
                fallbackMessage: t('admin.teams.teamManagement.errors.updateFailed')
            });
        }
    };

    return (
        <CollapsibleSection
            title={t('admin.teams.teamManagement.billingInformation')}
            icon={<CreditCard className="h-5 w-5"/>}
            defaultOpen={false}
            className="mb-4"
        >
                <div className="flex items-center gap-3 pb-4 border-b">
                    <div className="flex-1">
                        <p className="text-muted-foreground text-sm">
                            {t('admin.teams.teamManagement.billingDescription')}
                        </p>
                    </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="name">{t('admin.teams.teamManagement.teamName')}</Label>
                    <Input
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Team name"
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="billingEmail">{t('admin.teams.teamManagement.billingEmail')}</Label>
                        <Input
                            id="billingEmail"
                            type="email"
                            value={billingEmail}
                            onChange={(e) => setBillingEmail(e.target.value)}
                            placeholder="billing@example.com"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="phone">{t('admin.teams.teamManagement.phoneNumber')}</Label>
                        <Input
                            id="phone"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+41 123 456 7890"
                        />
                    </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="invoiceLanguage">{t('admin.teams.teamManagement.invoiceLanguage')}</Label>
                     <Select
                         value={invoiceLanguage || ""}
                         onValueChange={setInvoiceLanguage}
                     >
                        <SelectTrigger id="invoiceLanguage">
                            <SelectValue placeholder="Select language"/>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="en">English</SelectItem>
                            <SelectItem value="de">Deutsch</SelectItem>
                            <SelectItem value="fr">Francais</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="billingStreetAddress">{t('admin.teams.teamManagement.billingAddress')}</Label>
                    <Input
                        id="billingStreetAddress"
                        value={billingStreetAddress}
                        onChange={(e) => setBillingStreetAddress(e.target.value)}
                        placeholder="Street address"
                    />
                </div>

                <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="billingCity">{t('admin.teams.teamManagement.city')}</Label>
                        <Input
                            id="billingCity"
                            value={billingCity}
                            onChange={(e) => setBillingCity(e.target.value)}
                            placeholder="City"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="billingState">{t('admin.teams.teamManagement.state')}</Label>
                        <Input
                            id="billingState"
                            value={billingState}
                            onChange={(e) => setBillingState(e.target.value)}
                            placeholder="State/Province"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="billingPostalCode">{t('admin.teams.teamManagement.postalCode')}</Label>
                        <Input
                            id="billingPostalCode"
                            value={billingPostalCode}
                            onChange={(e) => setBillingPostalCode(e.target.value)}
                            placeholder="Postal code"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="billingCountry">{t('admin.teams.teamManagement.country')}</Label>
                        <Input
                            id="billingCountry"
                            value={billingCountry}
                            onChange={(e) => setBillingCountry(e.target.value)}
                            placeholder="Country"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="vatNumber">{t('admin.teams.teamManagement.vatNumber')}</Label>
                        <Input
                            id="vatNumber"
                            value={vatNumber}
                            onChange={(e) => setVatNumber(e.target.value)}
                            placeholder="VAT number"
                        />
                    </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="website">{t('admin.teams.teamManagement.website')}</Label>
                    <Input
                        id="website"
                        type="url"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        placeholder="https://example.com"
                    />
                </div>

            <CollapsibleSection title="Stripe">
                        <div className="space-y-2">
                            <Label htmlFor="stripeCustomerId">Stripe Customer ID</Label>
                            <Input
                                id="stripeCustomerId"
                                value={stripeCustomerId}
                                onChange={(e) => setStripeCustomerId(e.target.value)}
                                placeholder="Not set"
                            />
                        </div>
                        <Button
                            onClick={() => {
                                if (redirectData?.redirectUrl) {
                                    window.open(redirectData.redirectUrl, "_blank");
                                }
                            }}
                            disabled={!canOpenStripe || isLoadingRedirect || !redirectData?.redirectUrl}
                            variant="outline"
                            className="w-full"
                            title={!canOpenStripe ? "Set billing email or customer ID to open Stripe" : ""}
                        >
                            <ExternalLink className="h-4 w-4 mr-2"/>
                            {isLoadingRedirect ? "Loading..." : "Open in Stripe"}
                        </Button>
            </CollapsibleSection>

                <Button
                    onClick={handleSaveBillingInfo}
                    disabled={isUpdating || !hasChanges()}
                    variant="default"
                    className="w-full"
                >
                    {isUpdating ? "Saving..." : "Save Changes"}
                </Button>
        </CollapsibleSection>
    );
}
